import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Icons } from '../constants';
import { Board } from '../types';

interface Command {
    id: string;
    label: string;
    category: 'board' | 'action' | 'navigation' | 'settings';
    icon: React.FC<{ className?: string }>;
    shortcut?: string;
    action: () => void;
}

interface CommandPaletteProps {
    isOpen: boolean;
    onClose: () => void;
    boards: Board[];
    onSelectBoard: (boardId: string) => void;
    onCreateBoard: () => void;
    onToggleTheme: () => void;
    currentView: 'home' | 'canvas';
    onGoHome?: () => void;
}

export const CommandPalette = ({
    isOpen,
    onClose,
    boards,
    onSelectBoard,
    onCreateBoard,
    onToggleTheme,
    currentView,
    onGoHome,
}: CommandPaletteProps) => {
    const [query, setQuery] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    // Focus input when opened
    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
        if (!isOpen) {
            setQuery('');
        }
    }, [isOpen]);

    // Define commands
    const commands: Command[] = useMemo(() => {
        const cmds: Command[] = [
            {
                id: 'create-board',
                label: 'Create New Board',
                category: 'action',
                icon: Icons.Plus,
                shortcut: 'N',
                action: () => { onCreateBoard(); onClose(); },
            },
            {
                id: 'toggle-theme',
                label: 'Toggle Dark Mode',
                category: 'settings',
                icon: Icons.Moon,
                action: () => { onToggleTheme(); onClose(); },
            },
        ];

        // Add "Go Home" if in canvas view
        if (currentView === 'canvas' && onGoHome) {
            cmds.push({
                id: 'go-home',
                label: 'Go to Home',
                category: 'navigation',
                icon: Icons.Home,
                shortcut: 'H',
                action: () => { onGoHome(); onClose(); },
            });
        }

        // Add board navigation commands
        boards.filter(b => !b.isDeleted).forEach(board => {
            cmds.push({
                id: `board-${board.id}`,
                label: `Open: ${board.title}`,
                category: 'board',
                icon: Icons.File,
                action: () => { onSelectBoard(board.id); onClose(); },
            });
        });

        return cmds;
    }, [boards, onCreateBoard, onClose, onToggleTheme, currentView, onGoHome, onSelectBoard]);

    // Filter commands based on query
    const filteredCommands = useMemo(() => {
        if (!query.trim()) return commands;
        const lowerQuery = query.toLowerCase();
        return commands.filter(cmd =>
            cmd.label.toLowerCase().includes(lowerQuery) ||
            cmd.category.toLowerCase().includes(lowerQuery)
        );
    }, [commands, query]);

    // Group by category
    const groupedCommands = useMemo(() => {
        const groups: Record<string, Command[]> = {};
        filteredCommands.forEach(cmd => {
            if (!groups[cmd.category]) groups[cmd.category] = [];
            groups[cmd.category].push(cmd);
        });
        return groups;
    }, [filteredCommands]);

    // Handle keyboard navigation
    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => {
        setSelectedIndex(0);
    }, [query]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            onClose();
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex(prev => Math.min(prev + 1, filteredCommands.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex(prev => Math.max(prev - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (filteredCommands[selectedIndex]) {
                filteredCommands[selectedIndex].action();
            }
        }
    }, [onClose, filteredCommands, selectedIndex]);

    if (!isOpen) return null;

    const categoryLabels: Record<string, string> = {
        action: 'Actions',
        board: 'Boards',
        navigation: 'Navigation',
        settings: 'Settings',
    };

    let globalIndex = 0;

    return (
        <div
            className="fixed inset-0 bg-black/20 dark:bg-black/50 backdrop-blur-sm z-[2000] flex items-start justify-center pt-[15vh] animate-in fade-in duration-150"
            onClick={onClose}
        >
            <div
                className="w-full max-w-xl bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden animate-in zoom-in-95 slide-in-from-top-4 duration-200"
                onClick={e => e.stopPropagation()}
            >
                {/* Search Input */}
                <div className="p-4 border-b border-slate-100 dark:border-slate-800">
                    <div className="flex items-center gap-3">
                        <Icons.Search className="w-5 h-5 text-slate-400" />
                        <input
                            ref={inputRef}
                            type="text"
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Search commands, boards..."
                            className="flex-1 bg-transparent outline-none text-slate-900 dark:text-white placeholder:text-slate-400 text-lg font-medium"
                        />
                        <kbd className="px-2 py-1 bg-slate-100 dark:bg-slate-800 rounded text-[10px] font-bold text-slate-500 dark:text-slate-400">ESC</kbd>
                    </div>
                </div>

                {/* Commands List */}
                <div className="max-h-[50vh] overflow-y-auto thin-scrollbar p-2">
                    {Object.entries(groupedCommands).map(([category, cmds]: [string, Command[]]) => (
                        <div key={category} className="mb-4 last:mb-0">
                            <p className="px-3 py-2 text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                                {categoryLabels[category] || category}
                            </p>
                            {cmds.map(cmd => {
                                const isSelected = globalIndex === selectedIndex;
                                const currentIndex = globalIndex;
                                globalIndex++;
                                return (
                                    <button
                                        key={cmd.id}
                                        onClick={cmd.action}
                                        onMouseEnter={() => setSelectedIndex(currentIndex)}
                                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${isSelected
                                            ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400'
                                            : 'hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300'
                                            }`}
                                    >
                                        <div className={`p-2 rounded-lg ${isSelected ? 'bg-indigo-100 dark:bg-indigo-900/50' : 'bg-slate-100 dark:bg-slate-800'}`}>
                                            <cmd.icon className="w-4 h-4" />
                                        </div>
                                        <span className="flex-1 text-left font-medium text-sm">{cmd.label}</span>
                                        {cmd.shortcut && (
                                            <kbd className="px-2 py-1 bg-slate-100 dark:bg-slate-800 rounded text-[10px] font-bold text-slate-500 dark:text-slate-400">
                                                {cmd.shortcut}
                                            </kbd>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    ))}

                    {filteredCommands.length === 0 && (
                        <div className="py-12 text-center">
                            <Icons.Search className="w-8 h-8 text-slate-300 dark:text-slate-700 mx-auto mb-3" />
                            <p className="text-sm text-slate-400 dark:text-slate-500">No commands found</p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-3 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                    <div className="flex items-center justify-center gap-6 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                        <span className="flex items-center gap-1">
                            <kbd className="px-1.5 py-0.5 bg-slate-200 dark:bg-slate-700 rounded">↑↓</kbd>
                            Navigate
                        </span>
                        <span className="flex items-center gap-1">
                            <kbd className="px-1.5 py-0.5 bg-slate-200 dark:bg-slate-700 rounded">↵</kbd>
                            Select
                        </span>
                        <span className="flex items-center gap-1">
                            <kbd className="px-1.5 py-0.5 bg-slate-200 dark:bg-slate-700 rounded">Esc</kbd>
                            Close
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};
